import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

interface GrantCreditsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userEmail: string;
  onSuccess: () => void;
}

export function GrantCreditsDialog({ open, onOpenChange, userId, userEmail, onSuccess }: GrantCreditsDialogProps) {
  const [amount, setAmount] = useState<string>('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount === 0) {
      toast({ title: 'Invalid amount', description: 'Amount must be a non-zero number', variant: 'destructive' });
      return;
    }
    if (Math.abs(numericAmount) > 10000) {
      toast({ title: 'Amount too large', description: 'Max ±10,000 per request', variant: 'destructive' });
      return;
    }
    if (reason.trim().length < 5) {
      toast({ title: 'Reason required', description: 'Please enter at least 5 characters', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-grant-credits', {
        body: { target_user_id: userId, amount: numericAmount, reason: reason.trim() },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({
        title: 'Credits updated',
        description: `${numericAmount > 0 ? 'Granted' : 'Deducted'} ${Math.abs(numericAmount)} credits to ${userEmail}`,
      });
      setAmount('');
      setReason('');
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'Failed to update credits',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Grant or deduct credits</DialogTitle>
          <DialogDescription>
            Adjust credits for <span className="font-medium">{userEmail}</span>. Use positive numbers to grant, negative to deduct. All changes are logged.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="amount">Amount (max ±10,000)</Label>
            <Input
              id="amount"
              type="number"
              placeholder="e.g. 50 or -10"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reason">Reason (required, min 5 chars)</Label>
            <Textarea
              id="reason"
              placeholder="e.g. Compensation for failed extraction batch on 22.04"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
